/**
 * R18ぼかし案内モーダルの承認状態をlocalStorageに記憶する。
 * `dockingOnboarding.ts` と同様、Storageが使えない環境(プライベート
 * モード等)でもクラッシュさせず「毎回初回扱い」へ縮退する。
 */

export const R18_BLUR_ONBOARDING_STORAGE_KEY =
  "nijiurachan-r18-blur-onboarding";

const CURRENT_VERSION = 1;

export type R18BlurOnboardingRecord = Readonly<{
  v: number;
  completedAt: string;
}>;

type OnboardingStorage = Pick<Storage, "getItem" | "setItem">;
type RemovableStorage = Pick<Storage, "removeItem">;

/** 案内モーダルを出すか。「OK」で承認した記録があるときだけ出さない。 */
export function shouldShowR18BlurOnboarding(
  storage: OnboardingStorage | null = browserStorage(),
): boolean {
  return readRecord(storage) === null;
}

export function completeR18BlurOnboarding(
  completedAt: string,
  storage: OnboardingStorage | null = browserStorage(),
): void {
  if (!storage) return;
  const record: R18BlurOnboardingRecord = {
    v: CURRENT_VERSION,
    completedAt,
  };
  try {
    storage.setItem(R18_BLUR_ONBOARDING_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage拒否時もReactのstate側でモーダルは閉じるため無視する
  }
}

const resetListeners = new Set<() => void>();

/**
 * reset実行の通知を購読する。Gateがリロード無しのSPA遷移中でも案内モーダルを
 * 再表示できるようにするための口 (全データクリア直後にshowR18が既定trueへ
 * 戻っても、ぼかし無しのまま閲覧が続かないようにする)。
 */
export function subscribeR18BlurOnboardingReset(
  listener: () => void,
): () => void {
  resetListeners.add(listener);
  return (): void => {
    resetListeners.delete(listener);
  };
}

/** 「全データをクリア」用。承認記録を消し、購読中のGateへ再表示を通知する。 */
export function resetR18BlurOnboarding(
  storage: RemovableStorage | null = browserStorage(),
): void {
  try {
    storage?.removeItem(R18_BLUR_ONBOARDING_STORAGE_KEY);
  } catch {
    // Storage拒否時は記録自体が残っていないため無視する
  }
  for (const listener of resetListeners) listener();
}

function readRecord(
  storage: OnboardingStorage | null,
): R18BlurOnboardingRecord | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(R18_BLUR_ONBOARDING_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    if (
      record.v !== CURRENT_VERSION ||
      typeof record.completedAt !== "string"
    ) {
      return null;
    }
    return { v: CURRENT_VERSION, completedAt: record.completedAt };
  } catch {
    return null;
  }
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
